import PaymentSDK, { AdyenPayment, IAdyenConfig } from '@reolink-web/payment-sdk';
import { EPaymentMethod } from '@reolink-cloud/interface';
import AdyenSchema from 'IMAGE/cart/adyen-scheme.png';
import AdyenCreditCardPng from 'IMAGE/cart/adyen-credit-cart.png';
import { createPayment, approval } from '@/api/payment';
import { Async, Optimization } from '@reolink-web/utils';
import { unfoldError } from '@/common/js/notice';
import { AxiosError } from 'axios';
import errorCodeMap from './config/errorCodeMapping.json';
import { lang } from '@/lang/reolink';
import { getErrorSymbol } from '@/common/js/utils';
import i18n from 'LANG';
import { apmMonitor } from '@/service/apmMonitor';
import { EApmTransactionName, EApmTransactionType } from '@/config/apm';

export interface IAdyenStyle {
    paymentMethods: NonNullable<IAdyenConfig['paymentMethodsResponse']>['paymentMethods'];
    region: string;
    clientKey: string;
    redirectUrl: string;
    lang: string;
}

/**
 * 订阅id/订单id必填其一
 */
export interface IInitCtx {

    /**
     * 订单 id
     */
    orderId?: string;

    /**
     * 订阅 id
     */
    subscriptionId?: string;

    /**
     * 支付配置
     */
    cfg: IAdyenStyle;
    
    handleSuccess: ( paymentId: string, isPaymentId?: boolean ) => unknown;
    
    /**
     * 订单国家。adyen scheme支付按钮根据订单国家显示不同的
     */
    orderCountry?: string;
    /**
     * 订单过期回调
     */
    onOrderExpired?: () => void;
    /**
     * 错误回调
     */
    onError?: ( error: unknown, errorMsg: string ) => void;
    /**
     * 是否展示默认报错。兼容性改动
     * @default true
     */
    showDefaultError?: boolean;
    /**
     * action回调
     */
    onAction?: ( action: any ) => void;
    /**
     * additionalDetails回调
     */
    onAdditionalDetails?: IAdyenConfig['onAdditionalDetails'];
}

export interface IPaymentMethodHandler {
    // 支付 sdk 初始化
    init( ctx: IInitCtx ): Promise<void>;

    /**
     * @returns 支付实例
     */
    getInstance: () => AdyenPayment | null;
}

export class AdyenHandler implements IPaymentMethodHandler {

    private _instance: AdyenPayment | null = null;
    
    private _ctx: IInitCtx = {} as IInitCtx;
    
    private _paymentId = '';

    public constructor(
        private readonly _sdk: PaymentSDK,
    ) { }

    public async init( ctx: IInitCtx ): Promise<void> {

        const adyenCfg = ctx.cfg;
        this._ctx = ctx;

        if ( !adyenCfg ) return;

        const adyenConfig: IAdyenConfig = {
            clientKey: adyenCfg.clientKey,
            environment: adyenCfg.region,
            locale: lang,
            paymentMethodsResponse: {
                paymentMethods: adyenCfg.paymentMethods,
            },
            analytics: {
                enabled: false,
            },
            showPayButton: false,
            paymentMethodsConfiguration:{
                scheme: {
                    hasHolderName: true,
                }
            },
            onSubmit: this.onSubmit.bind( this ),
            onAdditionalDetails: this.onAdditionalDetails.bind( this ),
            onError: this.handleError.bind( this ),
        };

        try {

            const schemeButtonText = ( ctx.orderCountry === 'FR' ? i18n.t( 'pay.paymentButtonText.adyenSchemeCBText' ) : i18n.t( 'pay.paymentButtonText.adyenSchemeCreditCardText' )) as string;

            // 初始化adyen
            this._instance = this._sdk.createAdyen( adyenConfig, {
                /** 指定特定支付方式的语言包 */
                langPackage: {
                    'adyen-scheme': schemeButtonText,
                },
                getPaymentImage: ( type ) => {

                    if ( type === 'scheme' ) {

                        return ctx.orderCountry === 'FR' ? AdyenSchema : AdyenCreditCardPng;
                    
                    }
                    
                    return '';
                
                }
            });

            await this._instance.init();
        
        }
        catch ( error ) {

            console.error( 'init', error );
            this.handleError( error );
        
        }

    }

    public getInstance(): AdyenPayment | null {

        return this._instance;
    
    }

    private handleError( error: unknown ): void {

        if (( error as { name?: string; })?.name === 'CANCEL' ) return;
        const errorSymbol = getErrorSymbol( error );
        switch ( errorSymbol ) {

            case 'order_expired':
                this._ctx.onOrderExpired?.();
                return;
        
        }
        console.error( 'onError', error );
        const resultCode = ( error as AxiosError )?.response?.data?.error?.metadata?.resultCode;

        const i18nKey = `pay.adyen_error.${errorCodeMap[resultCode] ?? errorCodeMap['default']}`;

        if ( this._ctx.showDefaultError ?? true ) {

            unfoldError( i18nKey );

        }

        this._ctx.onError?.(
            error,
            i18n.t( i18nKey ) as string
        );
    
    }
    
    private readonly onSubmit: NonNullable<IAdyenConfig['onSubmit']> = Optimization.debounce(( state, component ) => {

        apmMonitor.startOrRenameTransaction(
            EApmTransactionName.LEVEL1_ORDER_PAY_ADYEN,
            EApmTransactionType.PAYMENT_PROCESS
        );
        const stateData = state.data;
        if ( !stateData ) {

            console.warn( 'No state payment data' );
            return;
                
        }
        Async.invokeAsync( async () => {

            try {

                const sessionRes = await createPayment({
                    order: this._ctx?.orderId,
                    subscriptionId: this._ctx?.subscriptionId,
                    redirectUrl: this._ctx.cfg.redirectUrl,
                    paymentMethod: EPaymentMethod.ADYEN,
                    lang: this._ctx.cfg.lang,
                    extraData: stateData,
                });
                this._paymentId = sessionRes?.paymentId ?? '';
                const action = sessionRes?.details.action;

                // 如果有 action 则交给 adyen 处理
                if ( action ) {

                    const type = component?._node?.getAttribute?.( 'payment-type' );
                    if ( type && typeof type === 'string' ) {

                        document.getElementById( `reo-adyen-${type}-secondary-btn` )?.remove();
                        component.handleAction( action, { challengeWindowSize: '05' });
                        console.log( 'handle adyen action', action );
                    
                        this._ctx.onAction?.( action );

                    }
                    
                }
                // 否则直接查询支付结果
                else {

                    this._ctx.handleSuccess?.( this._paymentId, true );

                }
                
            }
            catch ( error: unknown ) {

                /** 
                 * 重置该支付方式的状态，避免影响下次重载。需要先卸载再重载,不然会导致表单无法输入,或者用户名会被保留上一次的输入记录。
                 * */
                component?.unmount();
                component?.remount();
                console.error( error );
                this.handleError( error );
                
            }
                
        });

    }, 500 );
    
    private readonly onAdditionalDetails: NonNullable<IAdyenConfig['onAdditionalDetails']> = Optimization.debounce(( state, component ) => {

        apmMonitor.startOrRenameTransaction(
            EApmTransactionName.LEVEL1_ORDER_PAY_ADYEN,
            EApmTransactionType.PAYMENT_PROCESS
        );

        Async.invokeAsync( async () => {

            try {

                await approval(
                    undefined,
                    this._paymentId,
                    {
                        ...state.data
                    },
                );
                this._ctx.handleSuccess?.( this._paymentId, true );
                    
            } catch ( error ) {

                // update，相当于同一个组件实例，先unmount再remount
                component?.update({});
                console.error( error );
                this.handleError( error );
                    
            }
            finally {

                this._ctx.onAdditionalDetails?.( state, component );
            
            }
                
        });
            
    }, 500 );

}
